import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, LogIn, LogOut, Coffee, Play, RefreshCw, Calendar, User, ChevronLeft, ChevronRight, ArrowLeft, Shield, Users, UserCheck } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

export default function FichadorPanel() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [usersSummary, setUsersSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  // Determinar nivel de acceso
  const isAdmin = ['superadmin', 'admin'].includes(user?.role);
  const isRevisor = user?.role === 'revisor';
  const isUser = !isAdmin && !isRevisor;

  // Reloj en tiempo real
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Cargar datos
  const loadData = async () => {
    setLoading(true);
    try {
      // El backend filtra segun el rol del usuario
      const params = { startDate: selectedDate, endDate: selectedDate };

      // Si es usuario normal, solo pide sus propios datos
      if (isUser) {
        params.userId = user.id;
      }

      const res = await api.get('/api/fichador/history', { params });

      if (res.data.success && res.data.history.length > 0) {
        const dayData = res.data.history[0];
        let filteredEntries = dayData.entries || [];

        // Filtrado adicional en frontend segun rol
        if (isUser) {
          // Usuario solo ve sus propios fichajes
          filteredEntries = filteredEntries.filter(e => e.userId === user.id);
        } else if (isRevisor && user.departments) {
          // Revisor ve los de su departamento
          // Por ahora mostramos todos hasta tener la relacion usuario-departamento en fichajes
          // TODO: Filtrar por departamento cuando se agregue esa info
        }
        // Admin y superadmin ven todo

        setEntries(filteredEntries);

        // Calcular resumen por usuario
        const summary = {};
        for (const entry of filteredEntries) {
          if (!summary[entry.userId]) {
            summary[entry.userId] = {
              userId: entry.userId,
              userName: entry.userName,
              status: 'out',
              clockInTime: null,
              entries: [],
              totalMinutes: 0
            };
          }
          summary[entry.userId].entries.push(entry);

          // Actualizar estado
          if (entry.type === 'clock_in') {
            summary[entry.userId].status = 'working';
            summary[entry.userId].clockInTime = entry.timestamp;
          } else if (entry.type === 'clock_out') {
            summary[entry.userId].status = 'out';
          } else if (entry.type === 'break_start') {
            summary[entry.userId].status = 'break';
          } else if (entry.type === 'break_end') {
            summary[entry.userId].status = 'working';
          }
        }

        // Calcular tiempo trabajado
        for (const odI in summary) {
          const usr = summary[odI];
          let workStart = null;
          let total = 0;

          for (const e of usr.entries) {
            if (e.type === 'clock_in') {
              workStart = new Date(e.timestamp);
            } else if (e.type === 'break_start' && workStart) {
              total += (new Date(e.timestamp) - workStart);
              workStart = null;
            } else if (e.type === 'break_end') {
              workStart = new Date(e.timestamp);
            } else if (e.type === 'clock_out' && workStart) {
              total += (new Date(e.timestamp) - workStart);
              workStart = null;
            }
          }

          // Si sigue trabajando, sumar hasta ahora
          if (workStart && usr.status === 'working') {
            total += (Date.now() - workStart);
          }

          usr.totalMinutes = Math.floor(total / 1000 / 60);
        }

        setUsersSummary(summary);
      } else {
        setEntries([]);
        setUsersSummary({});
      }
    } catch (err) {
      console.error('Error cargando fichajes:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // Refrescar cada 30 segundos
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [selectedDate]);

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  };

  const formatMinutes = (mins) => {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return `${h}h ${m}m`;
  };

  const getEntryIcon = (type) => {
    switch (type) {
      case 'clock_in': return <LogIn className="w-4 h-4 text-green-600" />;
      case 'clock_out': return <LogOut className="w-4 h-4 text-red-600" />;
      case 'break_start': return <Coffee className="w-4 h-4 text-yellow-600" />;
      case 'break_end': return <Play className="w-4 h-4 text-blue-600" />;
      default: return <Clock className="w-4 h-4 text-gray-600" />;
    }
  };

  const getEntryLabel = (type) => {
    switch (type) {
      case 'clock_in': return 'Entrada';
      case 'clock_out': return 'Salida';
      case 'break_start': return 'Pausa';
      case 'break_end': return 'Vuelta';
      default: return type;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'working': return 'bg-green-500';
      case 'break': return 'bg-yellow-500';
      default: return 'bg-gray-400';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'working': return 'Trabajando';
      case 'break': return 'En pausa';
      default: return 'Fuera';
    }
  };

  const changeDate = (days) => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + days);
    setSelectedDate(d.toISOString().split('T')[0]);
  };

  const isToday = selectedDate === new Date().toISOString().split('T')[0];

  const usersArray = Object.values(usersSummary).sort((a, b) => {
    const order = { working: 0, break: 1, out: 2 };
    return order[a.status] - order[b.status];
  });

  // Icono y texto segun nivel de acceso
  const getAccessInfo = () => {
    if (isAdmin) return { icon: Shield, text: 'Vista completa', color: 'text-purple-600 bg-purple-50' };
    if (isRevisor) return { icon: Users, text: 'Vista departamento', color: 'text-blue-600 bg-blue-50' };
    return { icon: UserCheck, text: 'Mis fichajes', color: 'text-green-600 bg-green-50' };
  };

  const accessInfo = getAccessInfo();
  const AccessIcon = accessInfo.icon;

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title="Volver al Dashboard"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <img src="/logo-infoticketing.png" alt="Logo" className="h-10" />
              <div>
                <h1 className="text-xl font-bold text-gray-900">Fichador</h1>
                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${accessInfo.color}`}>
                    <AccessIcon className="w-3 h-3" />
                    {accessInfo.text}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-3xl font-mono font-bold text-gray-900">
                  {currentTime.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                </p>
                <p className="text-sm text-gray-500">
                  {currentTime.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Selector de fecha */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <button
              onClick={() => changeDate(-1)}
              className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg shadow-sm">
              <Calendar className="w-5 h-5 text-gray-500" />
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="border-none focus:ring-0 text-gray-900 font-medium"
              />
            </div>
            <button
              onClick={() => changeDate(1)}
              className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
              disabled={isToday}
            >
              <ChevronRight className={`w-5 h-5 ${isToday ? 'text-gray-300' : ''}`} />
            </button>
            {!isToday && (
              <button
                onClick={() => setSelectedDate(new Date().toISOString().split('T')[0])}
                className="px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              >
                Hoy
              </button>
            )}
          </div>
          <button
            onClick={loadData}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Panel de usuarios - Solo si hay mas de 1 usuario o es admin */}
          {(isAdmin || isRevisor || usersArray.length > 0) && (
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="px-4 py-3 bg-gray-50 border-b">
                  <h2 className="font-semibold text-gray-900">
                    {isUser ? 'Mi estado' : 'Estado del equipo'}
                  </h2>
                  <p className="text-sm text-gray-500">{usersArray.length} {usersArray.length === 1 ? 'persona' : 'personas'}</p>
                </div>
                <div className="divide-y max-h-[600px] overflow-y-auto">
                  {loading ? (
                    <div className="p-8 text-center text-gray-500">
                      <RefreshCw className="w-8 h-8 mx-auto mb-2 animate-spin" />
                      Cargando...
                    </div>
                  ) : usersArray.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                      <User className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      Sin fichajes este dia
                    </div>
                  ) : (
                    usersArray.map(usr => (
                      <div key={usr.userId} className="p-4 hover:bg-gray-50 transition-colors">
                        <div className="flex items-center gap-3">
                          <div className={`w-3 h-3 rounded-full ${getStatusColor(usr.status)}`} />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 truncate">{usr.userName}</p>
                            <p className="text-sm text-gray-500">
                              {getStatusLabel(usr.status)}
                              {usr.status !== 'out' && usr.clockInTime && (
                                <span> desde {formatTime(usr.clockInTime)}</span>
                              )}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-mono font-semibold text-gray-900">
                              {formatMinutes(usr.totalMinutes)}
                            </p>
                            <p className="text-xs text-gray-500">trabajado</p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Timeline de fichajes */}
          <div className={isUser && usersArray.length <= 1 ? 'lg:col-span-3' : 'lg:col-span-2'}>
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 border-b">
                <h2 className="font-semibold text-gray-900">
                  {isUser ? 'Mis fichajes' : 'Registro de fichajes'}
                </h2>
                <p className="text-sm text-gray-500">{entries.length} registros</p>
              </div>
              <div className="divide-y max-h-[600px] overflow-y-auto">
                {loading ? (
                  <div className="p-8 text-center text-gray-500">
                    <RefreshCw className="w-8 h-8 mx-auto mb-2 animate-spin" />
                    Cargando...
                  </div>
                ) : entries.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    Sin registros este dia
                  </div>
                ) : (
                  [...entries].reverse().map(entry => (
                    <div key={entry.id} className="p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex items-center gap-4">
                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                          {getEntryIcon(entry.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900">{entry.userName}</p>
                          <p className="text-sm text-gray-500">{getEntryLabel(entry.type)}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-mono font-semibold text-gray-900">
                            {formatTime(entry.timestamp)}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Resumen del dia - Solo para admin/revisor con multiples usuarios */}
        {(isAdmin || isRevisor) && usersArray.length > 0 && (
          <div className="mt-6 grid grid-cols-3 gap-4">
            <div className="bg-green-50 rounded-xl p-4 border border-green-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center">
                  <Play className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-700">
                    {usersArray.filter(u => u.status === 'working').length}
                  </p>
                  <p className="text-sm text-green-600">Trabajando</p>
                </div>
              </div>
            </div>
            <div className="bg-yellow-50 rounded-xl p-4 border border-yellow-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-yellow-500 flex items-center justify-center">
                  <Coffee className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-yellow-700">
                    {usersArray.filter(u => u.status === 'break').length}
                  </p>
                  <p className="text-sm text-yellow-600">En pausa</p>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gray-400 flex items-center justify-center">
                  <LogOut className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-700">
                    {usersArray.filter(u => u.status === 'out').length}
                  </p>
                  <p className="text-sm text-gray-600">Han salido</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Resumen personal para usuario */}
        {isUser && usersArray.length > 0 && (
          <div className="mt-6 bg-white rounded-xl shadow-sm p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Resumen del dia</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-3xl font-mono font-bold text-gray-900">
                  {formatMinutes(usersArray[0]?.totalMinutes || 0)}
                </p>
                <p className="text-sm text-gray-500 mt-1">Tiempo trabajado</p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-3xl font-bold text-gray-900">
                  {entries.filter(e => e.type === 'break_start').length}
                </p>
                <p className="text-sm text-gray-500 mt-1">Pausas realizadas</p>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
